import React, { useState, useEffect, ReactNode } from "react";
import Button from "../Button";
import Stack from "../Stack";
import Box from "../Box";
import Spacer from "../Spacer";
import Typography from "../Typography";
import { Alert, Platform, ActivityIndicator } from "react-native";
import { color, Theme } from "@sloth-system/styled-utils";
import { formatPrice } from "digiwash-core";
import { conditonal_values } from "./CouponInput-helpers";
import { Input } from "..";

interface PropTypes<T = any> {
  reduction?: number;
  workspaceName?: string;
  code?: string;
  loading?: boolean;
  onRemove?: () => Promise<T>;
  onAdd?: (code: string) => Promise<T>;
  onFocus?: () => void;
  onBlur?: () => void;
  onChange?: () => void;
  prefix?: ReactNode;
  errorRenderer?: (args: messageType[]) => void;
  theme: Theme;
  switchGroupsIsLoading?: boolean;
}

interface messageType {
  text: string;
  type: string;
  color: string;
}

function CouponInput<T>(props: PropTypes<T>) {
  // States
  let [couponInput, setCouponInput] = useState("");
  let [inputMode, setInputMode] = useState<"normal" | "filled" | "error">(
    "normal"
  );
  let [errorMessage, setErrorMessage] = useState("");

  // Logic
  let canSubmitForm = !!couponInput && !props.loading;
  let onSubmit = () => {
    // Remove
    if (inputMode === "filled") {
      removeCodeWithConfirmation();
      return;
    }

    //  Add
    if (canSubmitForm) {
      if (props.onAdd) {
        props.onAdd(couponInput).catch((error) => {
          setErrorMessage(error);
          setInputMode("error");
        });
      }
    }
  };

  let removeCodeWithConfirmation = () => {
    let question_text = "از حذف کد تخفیف اطمینان دارید؟";
    let removeCoupon = () => {
      if (props.onRemove) {
        props
          .onRemove()
          .then(() => {
            setCouponInput("");
            setInputMode("normal");
          })
          .catch();
      }
    };

    if (Platform.OS === "web") {
      let confirmed = confirm(question_text);
      if (confirmed) {
        removeCoupon();
      }
    } else {
      Alert.alert(
        "حذف کد تخفیف",
        question_text,
        [
          {
            text: "انصراف",
            style: "cancel",
          },
          {
            text: "حذف",
            onPress: () => {
              removeCoupon();
            },
          },
        ],
        { cancelable: false }
      );
    }
  };

  let messages: messageType[] = [];
  if (props.reduction) {
    messages.push({
      type: "success",
      text: `مبلغ ${formatPrice(props.reduction)} تومان از سفارش شما کسر شد.`,
      color: "green.light900",
    });
  }
  if (errorMessage) {
    messages.push({
      type: "error",
      text: errorMessage,
      color: "red.main",
    });
  }

  // Hooks
  useEffect(() => {
    if (props.code) {
      setInputMode("filled");
      setCouponInput(props.code);
    } else {
      setInputMode("normal");
      setCouponInput("");
    }
  }, [props.code]);

  // Values
  let { styles, content } = conditonal_values(inputMode);
  let hasError = messages.length > 0 && !props.loading;
  let actionDisabled = !couponInput || props.loading;

  // Render
  if (props.switchGroupsIsLoading) {
    return (
      <ActivityIndicator
        color={color("primary.main")({
          theme: props.theme,
        })}
      />
    );
  }
  if (props.workspaceName && !props.code) {
    return (
      <Box bg="red.light600" br="xm" px="l" py="xm">
        <Stack distribution="space-between">
          <Typography color="red.dark">{`این گروه کاربری فعال نیست`}</Typography>
          {props.prefix && <Box>{props.prefix}</Box>}
        </Stack>
      </Box>
    );
  }

  if (props.workspaceName) {
    return (
      <Box bg="green.light500" br="xm" px="l" py="xm">
        <Stack distribution="space-between">
          <Typography color="common.black">{`کد تخفیف ${props.workspaceName} برای شما فعال شده است.`}</Typography>
          {props.prefix && <Box>{props.prefix}</Box>}
        </Stack>
      </Box>
    );
  }

  return (
    <>
      <Input
        bg={styles.bg_input}
        p={styles.padding_input}
        onChangeText={(text) => {
          if (inputMode === "error") {
            setInputMode("normal");
            setErrorMessage("");
          }
          setCouponInput(text);
          if (props.onChange) {
            props.onChange();
          }
        }}
        value={couponInput}
        placeholder="کد تخفیف خود را وارد کنید"
        onSubmit={onSubmit}
        disabled={inputMode === "filled"}
        action={
          <Button
            onPress={onSubmit}
            prefix={
              props.loading && (
                <>
                  <Spacer size="m" />
                  <ActivityIndicator
                    color={color(styles.action_button_loading)({
                      theme: props.theme,
                    })}
                  />
                  <Spacer size="m" />
                </>
              )
            }
            fill={styles.action_button}
            textColor={styles.action_button_text}
            disabled={actionDisabled}
          >
            {content.action_button}
          </Button>
        }
        onFocus={() => {
          if (props.onFocus) {
            props.onFocus();
          }
        }}
        onBlur={() => {
          if (props.onBlur) {
            props.onBlur();
          }
        }}
        prefix={props.prefix}
      />

      {hasError && props.errorRenderer
        ? props.errorRenderer(messages)
        : undefined}
    </>
  );
}

export default CouponInput;
